import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Switch from "components/basic/switch";
import TextField from "components/basic/textField";
import React from "react";

interface AdvanceFeatureProps {
  advanceFeatureData: AdvanceFeatureData;
  setAdvanceFeatureData: React.Dispatch<
    React.SetStateAction<AdvanceFeatureData>
  >;
}

export interface VehicleFinance {
  vehicleCost: number;
  interest: number;
  downPayment: number;
  paymentDuration: number;
  insurance: number;
}

export interface AdvanceFeatureData {
  includeCostOfVehicle: boolean;
  includeLease: boolean;
  includeInsurance: boolean;
  iceFinance: VehicleFinance;
  evFinance: VehicleFinance;
}

const AdvanceFeature: React.FC<AdvanceFeatureProps> = ({
  advanceFeatureData: data,
  setAdvanceFeatureData: setData,
}) => {
  const handleCheckboxChange = (
    key: keyof AdvanceFeatureData,
    value: boolean,
  ) => {
    if (key === "includeCostOfVehicle" && value === false)
      setData((prev) => ({ ...prev, [key]: value, includeLease: false }));
    else setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleTextFieldChange = (
    section: "iceFinance" | "evFinance",
    key: keyof VehicleFinance,
    value: string,
  ) => {
    setData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: parseFloat(value) },
    }));
  };

  const theme = useTheme();
  const { t } = useTranslation("calculator");

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          marginLeft: theme.spacing(-3),
        }}
      >
        <Switch
          checked={data.includeCostOfVehicle}
          label={t("advanced.vehicle_cost")}
          onChange={(i) => handleCheckboxChange("includeCostOfVehicle", i)}
        />
        <Switch
          disabled={!data.includeCostOfVehicle}
          checked={data.includeLease}
          label={t("advanced.lease")}
          onChange={(i) => handleCheckboxChange("includeLease", i)}
        />
      </div>

      <div
        style={{
          display: "flex",
          marginLeft: theme.spacing(-3),
        }}
      >
        <Switch
          checked={data.includeInsurance}
          label={t("advanced.insurance")}
          onChange={(i) => handleCheckboxChange("includeInsurance", i)}
        />
      </div>

      {/** Total cost of vehicle */}
      {data.includeCostOfVehicle && (
        <div
          style={{
            display: "flex",
            gap: theme.spacing(1),
            alignItems: "end",
          }}
        >
          <TextFieldRow
            label={t("advanced.total_ev")}
            name="vehicleCost"
            value={data.evFinance.vehicleCost}
            onChange={(key, value) =>
              handleTextFieldChange(
                "evFinance",
                key as keyof VehicleFinance,
                value,
              )
            }
            step={1000}
            startAdornment="$"
            isDisabled={false}
          />
          <TextFieldRow
            label={t("advanced.total_ice")}
            name="vehicleCost"
            value={data.iceFinance.vehicleCost}
            onChange={(key, value) =>
              handleTextFieldChange(
                "iceFinance",
                key as keyof VehicleFinance,
                value,
              )
            }
            step={1000}
            startAdornment="$"
            isDisabled={false}
          />
        </div>
      )}

      {/** Lease of vehicle */}
      {data.includeLease && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: theme.spacing(1),
              alignItems: "end",
            }}
          >
            <TextFieldRow
              label={t("advanced.ev_interest")}
              name="interest"
              isDisabled={false}
              value={data.evFinance.interest}
              onChange={(key, value) =>
                handleTextFieldChange(
                  "evFinance",
                  key as keyof VehicleFinance,
                  value,
                )
              }
              step={0.1}
              endAdornment="%"
            />
            <TextFieldRow
              label={t("advanced.ice_interest")}
              name="interest"
              isDisabled={false}
              value={data.iceFinance.interest}
              onChange={(key, value) =>
                handleTextFieldChange(
                  "iceFinance",
                  key as keyof VehicleFinance,
                  value,
                )
              }
              step={0.1}
              endAdornment="%"
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: theme.spacing(1),
              alignItems: "end",
            }}
          >
            <TextFieldRow
              label={t("advanced.ev_lease_duration")}
              name="paymentDuration"
              isDisabled={false}
              value={data.evFinance.paymentDuration}
              onChange={(key, value) =>
                handleTextFieldChange(
                  "evFinance",
                  key as keyof VehicleFinance,
                  value,
                )
              }
              step={6}
              endAdornment="M"
            />
            <TextFieldRow
              label={t("advanced.ice_lease_duration")}
              name="paymentDuration"
              isDisabled={false}
              value={data.iceFinance.paymentDuration}
              onChange={(key, value) =>
                handleTextFieldChange(
                  "iceFinance",
                  key as keyof VehicleFinance,
                  value,
                )
              }
              step={6}
              endAdornment="M"
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: theme.spacing(1),
              alignItems: "end",
            }}
          >
            <TextFieldRow
              label={t("advanced.ev_down_payment")}
              name="downPayment"
              isDisabled={false}
              value={data.evFinance.downPayment}
              onChange={(key, value) =>
                handleTextFieldChange(
                  "evFinance",
                  key as keyof VehicleFinance,
                  value,
                )
              }
              step={1000}
              startAdornment="$"
            />
            <TextFieldRow
              label={t("advanced.ice_down_payment")}
              name="downPayment"
              isDisabled={false}
              value={data.iceFinance.downPayment}
              onChange={(key, value) =>
                handleTextFieldChange(
                  "iceFinance",
                  key as keyof VehicleFinance,
                  value,
                )
              }
              step={1000}
              startAdornment="$"
            />
          </div>
        </div>
      )}

      {/** Insurance of vehicle */}
      {data.includeInsurance && (
        <div
          style={{
            display: "flex",
            gap: theme.spacing(1),
            alignItems: "end",
          }}
        >
          <TextFieldRow
            label={t("advanced.ev_insurance")}
            name="insurance"
            value={data.evFinance.insurance}
            onChange={(key, value) =>
              handleTextFieldChange(
                "evFinance",
                key as keyof VehicleFinance,
                value,
              )
            }
            step={50}
            startAdornment="$"
            isDisabled={false}
          />
          <TextFieldRow
            label={t("advanced.ice_insurance")}
            name="insurance"
            value={data.iceFinance.insurance}
            onChange={(key, value) =>
              handleTextFieldChange(
                "iceFinance",
                key as keyof VehicleFinance,
                value,
              )
            }
            step={50}
            startAdornment="$"
            isDisabled={false}
          />
        </div>
      )}
    </div>
  );
};

const TextFieldRow: React.FC<{
  label: string;
  name: keyof VehicleFinance;
  isDisabled: boolean;
  value: number;
  onChange: (key: keyof VehicleFinance, value: string) => void;
  step: number;
  startAdornment?: string;
  endAdornment?: string;
  width?: string;
}> = ({
  label,
  name,
  isDisabled,
  value,
  onChange,
  step,
  startAdornment,
  endAdornment,
  width = "150px",
}) => {
  const theme = useTheme();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <h4 style={{ width: width }}>{label}</h4>
      <TextField
        isDisabled={isDisabled}
        name={name}
        type="number"
        step={step}
        startAdornment={startAdornment ? <>{startAdornment}</> : undefined}
        endAdornment={endAdornment ? <>{endAdornment}</> : undefined}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        style={{ width: width, marginTop: theme.spacing(-1) }}
      />
    </div>
  );
};

export default AdvanceFeature;
